import React, { useState } from 'react';
import Moment from 'moment';
import Drawer from '../Common/Drawer/Drawer';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../Common/InputText';
import DatePicker from '../Common/DatePicker/DatePicker';
import Toggle from '../Common/Toggle/Toggle';
import { DATE_FORMAT } from '../../constants/constants';
import type {
    FilterOption,
    StatusFilterOption,
    Tag,
    TaskSearchRequest,
} from '../../apis/locationTasks/locationTask.types';

// Department options carry a disciplineId so selecting a discipline can cascade to its departments
type DepartmentOption = FilterOption & { disciplineId?: number };

interface FiltersColumnFilters {
    projects: FilterOption[];
    disciplines: FilterOption[];
    departments: DepartmentOption[];
    tags: Tag[];
    phases: FilterOption[];
    communities: FilterOption[];
    statuses: StatusFilterOption[];
}

interface LocalFilterParams {
    projectIds: number[];
    disciplineIds: number[];
    departmentIds: number[];
    phaseIds: number[];
    communityIds: number[];
    statusIds: number[];
    tagIds: number[];
    name: string;
    responsibleUserName: string;
    additionalInfo: string;
    targetDateStart: string | null;
    targetDateEnd: string | null;
    startDateStart: string | null;
    startDateEnd: string | null;
    isMilestone?: boolean | null;
}

interface ShowFiltersDrawerProps {
    onComplete: (params: Partial<TaskSearchRequest>) => void;
    onCancel: () => void;
    columnFilters: FiltersColumnFilters;
    searchParams: TaskSearchRequest;
    isStartDateActive: boolean;
    // Accepted for parity with the parent's call signature, but not used here.
    updateSearchParams?: (key: string | null, value: unknown, search?: boolean) => void;
}

const DEFAULT_LOCAL_PARAMS: LocalFilterParams = {
    projectIds: [],
    disciplineIds: [],
    departmentIds: [],
    phaseIds: [],
    communityIds: [],
    statusIds: [],
    tagIds: [],
    name: '',
    responsibleUserName: '',
    additionalInfo: '',
    targetDateStart: '',
    targetDateEnd: '',
    startDateStart: '',
    startDateEnd: '',
};

export const ShowFiltersDrawer = ({ onComplete, onCancel, columnFilters, searchParams, isStartDateActive }: ShowFiltersDrawerProps) => {
    const [localParams, setLocalParams] = useState<LocalFilterParams>(
        () => ({ ...DEFAULT_LOCAL_PARAMS, ...searchParams }) as unknown as LocalFilterParams
    );

    const onFormChange = (name: string, value: unknown) => {
        setLocalParams((prev) => ({ ...prev, [name]: value }) as LocalFilterParams);
    };

    const onDropdownChange = (value: string, name: string) => {
        const newValues = value ? value.split(',').map(Number).filter((n) => !Number.isNaN(n)) : [];
        onFormChange(name, newValues);
    };

    const onDisciplineChange = (value: string) => {
        const newDisciplines = value ? value.split(',').map(Number) : [];

        // select all departments for all selected disciplines
        const deptIds: number[] = [];
        for (const disciplineId of newDisciplines) {
            const depts = columnFilters.departments.filter((m) => m.disciplineId === disciplineId);
            deptIds.push(...depts.map((m) => m.id));
        }

        setLocalParams((prev) => ({
            ...prev,
            disciplineIds: newDisciplines,
            departmentIds: deptIds,
        }));
    };

    const onDepartmentChange = (selectedIds: string) => {
        const newDepartmentIds = selectedIds ? selectedIds.split(',').map(Number) : [];

        setLocalParams((prev) => {
            const oldDepartmentIds = prev.departmentIds.length > 0 ? prev.departmentIds : [];
            const disciplineIds = prev.disciplineIds.length > 0 ? [...prev.disciplineIds] : [];

            // if a new department was selected, make sure its discipline is selected
            if (newDepartmentIds.length > oldDepartmentIds.length) {
                const newDepartment = columnFilters.departments.find((m) => m.id === newDepartmentIds[newDepartmentIds.length - 1]);
                if (newDepartment?.disciplineId !== undefined && disciplineIds.indexOf(newDepartment.disciplineId) === -1) {
                    disciplineIds.push(newDepartment.disciplineId);
                }
            }

            return { ...prev, disciplineIds, departmentIds: newDepartmentIds };
        });
    };

    const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFormChange(e.currentTarget.name, e.currentTarget.value);
    };

    const onDateChange = (value: string, name: string) => {
        onFormChange(name, value);
    };

    const onToggleChange = (name: string, value: boolean) => {
        onFormChange(name, value === false ? null : value);
    };

    const targetStart = localParams.targetDateStart ? Moment(localParams.targetDateStart).format(DATE_FORMAT) : '';
    const targetEnd = localParams.targetDateEnd ? Moment(localParams.targetDateEnd).format(DATE_FORMAT) : '';
    const startStart = localParams.startDateStart ? Moment(localParams.startDateStart).format(DATE_FORMAT) : '';
    const startEnd = localParams.startDateEnd ? Moment(localParams.startDateEnd).format(DATE_FORMAT) : '';

    return (
        <Drawer
            isOpen={true}
            isWide={true}
            title='Filters'
            onSaveClick={() => onComplete(localParams as Partial<TaskSearchRequest>)}
            saveText='Update filters'
            onCancelClick={onCancel}>
            <div className='task-list-filters-drawer'>
                <Toggle name='isMilestone' text='Milestones Only' checked={localParams.isMilestone} onChange={onToggleChange} />
                {isStartDateActive && (
                    <div className='mb-3'>
                        <label>
                            <strong>Start Date Range</strong>
                        </label>
                        <div className='row'>
                            <div className='col-6 mt-2'>
                                <DatePicker name='startDateStart' label='Start' onChange={onDateChange} value={startStart} />
                            </div>
                            <div className='col-6 mt-2'>
                                <DatePicker name='startDateEnd' label='End' onChange={onDateChange} value={startEnd} />
                            </div>
                        </div>
                    </div>
                )}
                <div className='mb-3'>
                    <label>
                        <strong>Target Date Range</strong>
                    </label>
                    <div className='row'>
                        <div className='col-6 mt-2'>
                            <DatePicker name='targetDateStart' label='Start' onChange={onDateChange} value={targetStart} />
                        </div>
                        <div className='col-6 mt-2'>
                            <DatePicker name='targetDateEnd' label='End' onChange={onDateChange} value={targetEnd} />
                        </div>
                    </div>
                </div>
                <MultiSelectBox
                    name='projectIds'
                    label='Projects'
                    idField='id'
                    valueField='name'
                    options={columnFilters.projects}
                    selectedValues={localParams.projectIds.toString()}
                    isMulti={true}
                    onChange={onDropdownChange}
                />
                <MultiSelectBox
                    name='disciplineIds'
                    label='Disciplines'
                    idField='id'
                    valueField='name'
                    options={columnFilters.disciplines}
                    selectedValues={localParams.disciplineIds.toString()}
                    isMulti={true}
                    onChange={onDisciplineChange}
                />
                <MultiSelectBox
                    name='departmentIds'
                    label='Departments'
                    idField='id'
                    valueField='name'
                    options={columnFilters.departments}
                    selectedValues={localParams.departmentIds.toString()}
                    isMulti={true}
                    onChange={onDepartmentChange}
                />
                <MultiSelectBox
                    name='tagIds'
                    label='Tags'
                    idField='id'
                    valueField='name'
                    options={columnFilters.tags}
                    selectedValues={localParams.tagIds.toString()}
                    isMulti={true}
                    onChange={onDropdownChange}
                />
                <MultiSelectBox
                    name='phaseIds'
                    label='Phases'
                    idField='id'
                    valueField='name'
                    options={columnFilters.phases}
                    selectedValues={localParams.phaseIds.toString()}
                    isMulti={true}
                    onChange={onDropdownChange}
                />
                <MultiSelectBox
                    name='communityIds'
                    label='Communities'
                    idField='id'
                    valueField='name'
                    options={columnFilters.communities}
                    selectedValues={localParams.communityIds.toString()}
                    isMulti={true}
                    onChange={onDropdownChange}
                />
                <MultiSelectBox
                    name='statusIds'
                    label='Status'
                    idField='id'
                    valueField='name'
                    options={columnFilters.statuses}
                    selectedValues={localParams.statusIds.toString()}
                    isMulti={true}
                    onChange={onDropdownChange}
                />
                <InputText
                    name='name'
                    label='Task Name'
                    type='text'
                    value={localParams.name}
                    maxLength={20}
                    onChange={onTextChange}
                />
                <InputText
                    name='responsibleUserName'
                    label='Responsible User'
                    type='text'
                    value={localParams.responsibleUserName}
                    maxLength={20}
                    onChange={onTextChange}
                />
                <InputText
                    name='additionalInfo'
                    label='Additional Info'
                    type='text'
                    value={localParams.additionalInfo}
                    maxLength={20}
                    onChange={onTextChange}
                />
            </div>
        </Drawer>
    );
};

export default ShowFiltersDrawer;
